import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Heading,
  Divider,
  Icon,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Alert,
  AlertIcon,
  Container,
  useColorModeValue,
  Badge,
  Progress,
  useSteps,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepSeparator,
  StepTitle,
  StepDescription
} from '@chakra-ui/react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaFileContract, FaKey, FaSignature, FaCheck, FaBuilding } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import SignatureCanvas from 'react-signature-canvas';

const LabAgreement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const sigPad = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchParams] = useSearchParams();

  // Get lab data from location state or URL parameters
  const { labData, plan } = location.state || {};
  const labIdFromUrl = searchParams.get('labId');
  
  // State for fetched lab data
  const [fetchedLabData, setFetchedLabData] = useState(null);
  const [fetchedPlanData, setFetchedPlanData] = useState(null);
  const [isLoadingLabData, setIsLoadingLabData] = useState(false);
  
  // Mock data for development/testing when accessed directly
  const mockLabData = {
    id: 1,
    name: "ALASKA EMERGENCY MEDICINE ASSOCIATES",
  };
  
  const mockPlan = {
    id: "enterprise",
    name: "Enterprise",
    price: 999,
  };
  
  // Determine effective data: location state > fetched data > mock data
  const effectiveLabData = labData || fetchedLabData || mockLabData;
  const effectivePlan = plan || fetchedPlanData || mockPlan;
  
  const [formData, setFormData] = useState({
    customSecretKey: '',
    signerName: '',
    signerTitle: '',
    companyName: effectiveLabData?.name || '',
    agreementAccepted: false
  });
  
  const [loading, setLoading] = useState(false);
  const [signature, setSignature] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1 = Agreement Review, 2 = Form & Signature
  
  const steps = [
    { title: 'Review Agreement', description: 'Read the partnership terms' },
    { title: 'Setup & Sign', description: 'Create password and sign' }
  ];
  
  const { activeStep } = useSteps({
    index: currentStep - 1,
    count: steps.length,
  });

  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, white)',
    'linear(to-br, gray.900, gray.800)'
  );

  // Fetch lab data if labId is provided in URL
  useEffect(() => {
    const fetchLabData = async () => {
      if (!labIdFromUrl || labData) return; // Skip if we already have lab data from routing state
      
      setIsLoadingLabData(true);
      console.log('Fetching lab data for ID:', labIdFromUrl);
      
      try {
        // Fetch lab data
        const { data: lab, error: labError } = await supabase
          .from('labs')
          .select('*')
          .eq('id', labIdFromUrl)
          .single();

        if (labError) {
          console.error('Error fetching lab:', labError);
          throw labError;
        }

        if (!lab) {
          throw new Error('Lab not found');
        }

        setFetchedLabData(lab);

        // Determine plan based on lab's subscription_plan
        let planData = mockPlan; // default
        
        if (lab.subscription_plan) {
          // You can create a mapping of plan IDs to plan data, or fetch from a plans table
          const planMapping = {
            'basic': { id: 'basic', name: 'Basic', price: 299 },
            'professional': { id: 'professional', name: 'Professional', price: 599 },
            'enterprise': { id: 'enterprise', name: 'Enterprise', price: 999 }
          };
          
          planData = planMapping[lab.subscription_plan] || mockPlan;
        }

        setFetchedPlanData(planData);

      } catch (error) {
        console.error('Failed to fetch lab data:', error);
        toast({
          title: 'Error Loading Lab Data',
          description: error.message || 'Failed to load lab information. Using demo data.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        // Fall back to mock data - already handled by effectiveLabData logic
      } finally {
        setIsLoadingLabData(false);
      }
    };

    fetchLabData();
  }, [labIdFromUrl, labData, toast]);

  // Lab Services Agreement
  const agreementText = `
LAB SERVICES AGREEMENT
This Lab Services Agreement ("Agreement") is entered into as of the Effective Date by and between Athomeblooddraw.com ("Company") and the undersigned laboratory ("Laboratory").

1. Purpose
Company operates a workflow platform that connects mobile phlebotomists with laboratories for the purpose of specimen shipment and processing. This Agreement sets forth the terms under which Laboratory may participate on the platform, obtain visibility through advertising placement, and access related services.

2. Services Provided
2.1 Website Placement
Laboratory shall be listed on Company's website and platform directory based on the subscription tier selected. Placement is an advertising service only and is not tied to the number of specimens, reimbursement rates, or test volume.

2.2 Test Directory
Laboratory may upload its available tests, cash pricing, accepted insurances, and special test instructions to the platform's Test Directory. Company will provide Laboratory with a dedicated test directory page accessible to patients.

2.3 Shipment Processing
When mobile phlebotomists use the platform to send specimens to Laboratory, Company will generate shipping labels and facilitate logistics.

3. Fees
3.1 Monthly Placement Fee
Laboratory agrees to pay Company a flat monthly advertising fee based on tier selection:
● $99 per month (Basic)
● $299 per month (Standard)
● $999 per month (Premium)
This fee covers directory placement, advertising visibility, and the Test Directory feature.

3.2 Shipment Processing Fee
Laboratory agrees to pay a Shipment Processing Fee for each package shipped via the platform.
● Fees are calculated based on shipment weight, distance, origin, destination, and delivery speed.
● Fees cover all costs of shipping, packaging, handling, and logistics coordination.
● Shipment Processing Fees are invoiced separately from the Monthly Placement Fee.

3.3 Payment Terms
All fees are due monthly in advance. Shipment Processing Fees are billed in arrears based on usage. Payments shall be made electronically.

4. Compliance
4.1 No Referral or Volume-Based Compensation
Compensation under this Agreement is not conditioned on the volume or value of referrals, tests, or reimbursements. Fees are flat for advertising and variable only for shipment logistics.

4.2 Anti-Kickback Compliance
The parties intend to comply with all federal and state healthcare laws, including the federal Anti-Kickback Statute (42 U.S.C. §1320a-7b(b)). This Agreement does not involve per-specimen payments, percentages of reimbursement, or any form of remuneration for referrals.

4.3 Termination for Non-Compliance
Company may suspend or terminate this Agreement immediately if it reasonably believes Laboratory is not in compliance with applicable healthcare laws.

5. Term and Termination
This Agreement is month-to-month and may be terminated by either party upon thirty (30) days' written notice.

6. Representations and Warranties
Laboratory represents that it is duly licensed and compliant with applicable laws, including CLIA certification and state laboratory requirements.

7. Indemnification
Laboratory shall indemnify and hold harmless Company against any claims, damages, or liabilities arising out of Laboratory's non-compliance with laws, improper billing, or negligence.

8. Limitation of Liability
Company's liability under this Agreement shall be limited to the total amount paid by Laboratory in the three (3) months preceding the claim.

9. Miscellaneous
● Entire Agreement: This Agreement constitutes the full agreement between the parties.
● Governing Law: This Agreement shall be governed by the laws of the state where Company is headquartered.
● Assignment: Neither party may assign this Agreement without prior written consent.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.

Athomeblooddraw.com
By: [Digital Signature Required]
Name: [System Generated]
Title: [System Generated]
Date: [System Generated]

Laboratory
By: [Digital Signature Below]
Name: [Signer Name Required]
Title: [Signer Title Required]
Date: [System Generated]`;

  const handleSecretKeyChange = (e) => {
    let value = e.target.value.replace(/[^a-zA-Z0-9!@#$%&*]/g, '');
    
    // Limit to 8-16 characters for better security
    value = value.slice(0, 16);
    
    setFormData({ ...formData, customSecretKey: value });
  };

  const validatePassword = (password) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const isLongEnough = password.length >= 8;
    
    return {
      isValid: hasUppercase && hasLowercase && hasNumber && isLongEnough,
      hasUppercase,
      hasLowercase,
      hasNumber,
      isLongEnough
    };
  };

  const clearSignature = () => {
    sigPad.current.clear();
    setSignature(null);
  };

  const saveSignature = () => {
    if (sigPad.current.isEmpty()) {
      toast({
        title: 'Signature Required',
        description: 'Please provide your signature before proceeding.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    const signatureData = sigPad.current.toDataURL();
    setSignature(signatureData);
    onClose();
    
    toast({
      title: 'Signature Saved',
      description: 'Your signature has been captured successfully.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const passwordValidation = validatePassword(formData.customSecretKey);
    if (!passwordValidation.isValid) {
      const missing = [];
      if (!passwordValidation.hasUppercase) missing.push('uppercase letter');
      if (!passwordValidation.hasLowercase) missing.push('lowercase letter');
      if (!passwordValidation.hasNumber) missing.push('number');
      if (!passwordValidation.isLongEnough) missing.push('at least 8 characters');
      
      toast({
        title: 'Password Requirements Not Met',
        description: `Password must contain: ${missing.join(', ')}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    if (!signature) {
      toast({
        title: 'Signature Required',
        description: 'Please provide your digital signature.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!formData.signerName || !formData.signerTitle) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    
    try {
      // Check if password is already in use
      const { data: existingLab } = await supabase
        .from('labs')
        .select('id')
        .eq('secret_key', formData.customSecretKey)
        .single();
        
      if (existingLab) {
        toast({
          title: 'Password Already Exists',
          description: 'This password is already in use. Please choose a more secure password with uppercase, lowercase, and numbers.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      // Calculate next billing date (28 days from now)
      const nextBillingDate = new Date();
      nextBillingDate.setDate(nextBillingDate.getDate() + 28);

      // Generate portal slug
      const generatePortalSlug = (labName) => {
        return labName
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '')
          .replace(/\s+/g, '-')
          .substring(0, 50);
      };

      const portalSlug = generatePortalSlug(effectiveLabData.name);

      const updateData = {
        secret_key: formData.customSecretKey,
        subscription_plan: effectivePlan.id,
        subscription_status: 'active',
        monthly_fee: effectivePlan.price,
        enrollment_date: new Date().toISOString(),
        next_billing_date: nextBillingDate.toISOString(),
        portal_slug: portalSlug,
        partnership_status: 'active',
        agreement_signed: true,
        agreement_date: new Date().toISOString(),
        signer_name: formData.signerName,
        signer_title: formData.signerTitle,
        signature_data: signature
      };

      const { data, error } = await supabase
        .from('labs')
        .update(updateData)
        .eq('id', effectiveLabData.id)
        .select();


      if (error) {
        console.error('Database update error:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error(`No lab found with ID ${effectiveLabData.id}. The lab may not exist in the database.`);
      }

      toast({
        title: 'Agreement Signed Successfully!',
        description: `Welcome to the network! Your lab password is ${formData.customSecretKey}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Redirect to lab portal with the new password
        navigate(`/lab-portal/${portalSlug}`, {
          state: {
            isNewSignup: true,
          password: formData.customSecretKey,
          labName: effectiveLabData.name,
          plan: effectivePlan.name
        }
      });

    } catch (error) {
      console.error('Agreement submission error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to process agreement. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    
    setLoading(false);
  };

  // Show loading state when fetching lab data
  if (isLoadingLabData) {
    return (
      <Box minHeight="100vh" bg={bgGradient} display="flex" alignItems="center" justifyContent="center">
        <Container maxW="container.md" py={16}>
          <VStack spacing={4}>
            <Box animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
              <Icon as={FaFileContract} boxSize={12} color="blue.500" />
            </Box>
            <Text fontSize="xl" fontWeight="bold" color="gray.600">
              Loading Lab Agreement...
            </Text>
            <Text fontSize="md" color="gray.500">
              Fetching lab information and preparing your agreement
            </Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  // Only show error if we don't have effective data (which includes mock data)
  if (!effectiveLabData || !effectivePlan) {
    return (
      <Box minHeight="100vh" bg={bgGradient}>
        <Container maxW="container.md" py={16}>
          <Alert status="error">
            <AlertIcon />
            Missing lab or plan information. Please return to the signup process.
          </Alert>
        </Container>
      </Box>
    );
  }

  const renderStep1 = () => (
    <Box position="relative" w="full" h="full" bg="white">
      <Box
        w="100vw"
        h="100vh"
        overflowY="auto"
        p={0}
        bg="linear-gradient(135deg, blue.50, white, blue.50)"
        fontSize="lg"
        lineHeight="1.8"
      >
        {/* Header Section - Full Width */}
        <Box
          w="full"
          bg="linear-gradient(135deg, blue.600, blue.800)"
          color="white"
          py={16}
          px={8}
          textAlign="center"
          position="relative"
          overflow="hidden"
        >
          {/* Background Pattern */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgImage="radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 1px, transparent 1px), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)"
            bgSize="40px 40px"
            opacity={0.5}
          />
          <VStack spacing={6} position="relative" zIndex={1}>
            <Icon as={FaFileContract} boxSize={20} color="white" />
            <Heading size="4xl" fontWeight="black" textShadow="2px 2px 4px rgba(0,0,0,0.3)">
              LABORATORY PARTNERSHIP AGREEMENT
            </Heading>
            <Text fontSize="2xl" fontWeight="medium" opacity={0.9}>
              Official Legal Document - Please Read Carefully
            </Text>
            <Box w="200px" h="1px" bg="white" opacity={0.7} />
          </VStack>
        </Box>

        {/* Document Content - Full Width */}
        <Box w="full" px={0} py={0}>
          <VStack spacing={0} align="stretch">
            {/* Agreement Content */}
            <Box
              bg="white"
              p={16}
              borderRadius={0}
              border="none"
              fontSize="lg"
              lineHeight="2"
              fontFamily="serif"
              color="gray.800"
              boxShadow="none"
              w="full"
              minH="70vh"
            >
              <Box maxW="1200px" mx="auto" px={8}>
                <Text whiteSpace="pre-line">{agreementText}</Text>
              </Box>
            </Box>

            {/* Action Section - Full Width */}
            <Box
              w="full"
              bg="linear-gradient(135deg, blue.600, blue.800)"
              color="white"
              py={24}
              px={8}
              textAlign="center"
              position="relative"
              overflow="hidden"
            >
              {/* Background Pattern */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bgImage="radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 2px, transparent 2px), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 2px, transparent 2px)"
                bgSize="60px 60px"
                opacity={0.4}
              />
              
              <VStack spacing={12} position="relative" zIndex={1} maxW="1200px" mx="auto">
                <VStack spacing={6}>
                  <Heading size="3xl" fontWeight="black" textShadow="2px 2px 4px rgba(0,0,0,0.3)">
                    Electronic Signature Required
                  </Heading>
                  <Text fontSize="xl" opacity={0.9} maxW="3xl">
                    By proceeding, you acknowledge that you have read, understood, and agree to be bound by the terms of this agreement.
                      </Text>
                    </VStack>
                
                {/* Lab Info Box - Enhanced */}
                <Box
                  bg="rgba(255,255,255,0.95)"
                  backdropFilter="blur(20px)"
                  p={12}
                  borderRadius="3xl"
                  border="2px solid rgba(255,255,255,0.2)"
                  w="full"
                  maxW="600px"
                  boxShadow="0 20px 40px rgba(0,0,0,0.1)"
                >
                    <VStack spacing={6}>
                    <HStack spacing={6} w="full" justify="center">
                      <Icon as={FaBuilding} boxSize={12} color="blue.600" />
                      <VStack align="start" spacing={2}>
                        <Text fontSize="2xl" fontWeight="black" color="gray.800">
                          {effectiveLabData.name}
                        </Text>
                        <Text fontSize="lg" color="gray.600" fontWeight="medium">
                          Laboratory Partner
                        </Text>
                      </VStack>
                    </HStack>
                    
                    <Divider borderColor="blue.300" borderWidth="2px" />
                    
                    <HStack spacing={12} w="full" justify="center">
                      <VStack spacing={2}>
                        <Badge colorScheme="green" variant="solid" px={6} py={3} borderRadius="full" fontSize="xl">
                          {effectivePlan.name} Plan
                        </Badge>
                        <Text fontSize="md" color="gray.600" fontWeight="medium">Subscription Plan</Text>
                      </VStack>
                      <VStack spacing={2}>
                        <Text fontSize="4xl" fontWeight="black" color="green.600">
                          ${effectivePlan.price}
                        </Text>
                        <Text fontSize="md" color="gray.600" fontWeight="medium">/month</Text>
                      </VStack>
                    </HStack>
                  </VStack>
                </Box>

                {/* Action Button - Enhanced */}
                <Button
                  onClick={() => setCurrentStep(2)}
                  size="2xl"
                  py={16}
                  px={24}
                  fontSize="3xl"
                  fontWeight="black"
                  borderRadius="3xl"
                  bg="white"
                  color="blue.600"
                  _hover={{
                    bg: "blue.50",
                    transform: "translateY(-6px)",
                    boxShadow: "0 25px 50px rgba(0,0,0,0.3)"
                  }}
                  _active={{
                    transform: "translateY(-3px)",
                    boxShadow: "0 15px 30px rgba(0,0,0,0.2)"
                  }}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  boxShadow="0 20px 40px rgba(0,0,0,0.2)"
                  leftIcon={<Icon as={FaCheck} boxSize={10} />}
                  minW="500px"
                  minH="100px"
                >
                  I Accept Terms - Continue to Setup
                </Button>
              </VStack>
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* Floating Progress Overlay */}
      <Box
        position="fixed"
        top={6}
        right={6}
        bg="whiteAlpha.900"
        backdropFilter="blur(20px)"
        borderRadius="2xl"
        p={6}
        border="2px solid"
        borderColor="blue.200"
        boxShadow="2xl"
        zIndex={10}
      >
        <VStack spacing={4}>
          <HStack spacing={3}>
            <Icon as={FaFileContract} boxSize={5} color="blue.500" />
            <Text fontSize="md" fontWeight="bold" color="gray.800">
              Setup Progress
            </Text>
          </HStack>
          <Progress 
            value={(currentStep / steps.length) * 100} 
            size="md" 
            borderRadius="full"
            bg="gray.200"
            colorScheme="blue"
            w="full"
          />
          <Text fontSize="sm" color="gray.600" fontWeight="bold">
            {Math.round((currentStep / steps.length) * 100)}% Complete
          </Text>
        </VStack>
      </Box>
    </Box>
  );

  const renderStep2 = () => (
    <Box position="relative" w="full" h="full" bg="linear-gradient(135deg, green.400, green.600)">
      <Box
        w="full"
        h="full"
        overflowY="auto"
        p={8}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="center"
      >
        <VStack spacing={8} w="full" maxW="2xl">
          {/* Header */}
          <VStack spacing={6} textAlign="center" color="white">
            <Box
              bg="whiteAlpha.200"
              backdropFilter="blur(20px)"
              borderRadius="full"
              p={8}
              border="3px solid"
              borderColor="whiteAlpha.300"
            >
              <Icon as={FaSignature} boxSize={16} color="white" />
            </Box>
            <Heading size="3xl" fontWeight="black" textShadow="2px 2px 4px rgba(0,0,0,0.3)">
              Complete Your Setup
            </Heading>
            <Text fontSize="xl" opacity={0.9} maxW="2xl">
              Create your lab portal password and provide your digital signature to finalize the agreement.
            </Text>
          </VStack>

          {/* Form Card */}
          <Card bg="white" borderRadius="3xl" boxShadow="3xl" w="full" border="3px solid" borderColor="green.200">
            <CardBody p={12}>
                  <form id="lab-agreement-form" onSubmit={handleSubmit}>
                <VStack spacing={8}>
                      <FormControl isRequired>
                    <FormLabel fontSize="2xl" fontWeight="bold" color="gray.800">
                          <HStack>
                        <Icon as={FaKey} color="green.500" boxSize={8} />
                        <Text>Lab Portal Password</Text>
                          </HStack>
                        </FormLabel>
                        <Input
                          value={formData.customSecretKey}
                          onChange={handleSecretKeyChange}
                      placeholder="Enter 8-16 characters (uppercase, lowercase, numbers, !@#$%&*)"
                      size="2xl"
                      maxLength={16}
                      bg="gray.50"
                      border="3px solid"
                      borderColor="gray.200"
                      _focus={{ borderColor: "green.500", bg: "white", transform: "scale(1.02)" }}
                      fontSize="xl"
                      py={8}
                      borderRadius="xl"
                    />
                    <Text fontSize="sm" color="gray.600" mt={2}>
                      This will be your password to access the lab portal. Keep it secure!
                        </Text>
                      </FormControl>

                      <FormControl isRequired>
                    <FormLabel fontSize="2xl" fontWeight="bold" color="gray.800">Signer Name</FormLabel>
                        <Input
                          value={formData.signerName}
                          onChange={(e) => setFormData({ ...formData, signerName: e.target.value })}
                      placeholder="Full name of person signing"
                      size="2xl"
                      bg="gray.50"
                      border="3px solid"
                      borderColor="gray.200"
                      _focus={{ borderColor: "green.500", bg: "white", transform: "scale(1.02)" }}
                      fontSize="xl"
                      py={8}
                      borderRadius="xl"
                        />
                      </FormControl>

                      <FormControl isRequired>
                    <FormLabel fontSize="2xl" fontWeight="bold" color="gray.800">Title/Position</FormLabel>
                        <Input
                          value={formData.signerTitle}
                          onChange={(e) => setFormData({ ...formData, signerTitle: e.target.value })}
                      placeholder="Your title or position at the lab"
                      size="2xl"
                      bg="gray.50"
                      border="3px solid"
                      borderColor="gray.200"
                      _focus={{ borderColor: "green.500", bg: "white", transform: "scale(1.02)" }}
                      fontSize="xl"
                      py={8}
                      borderRadius="xl"
                        />
                      </FormControl>

                      <FormControl isRequired>
                    <FormLabel fontSize="2xl" fontWeight="bold" color="gray.800">
                          <HStack>
                        <Icon as={FaSignature} color="green.500" boxSize={8} />
                            <Text>Digital Signature</Text>
                          </HStack>
                        </FormLabel>
                        <Button
                          onClick={onOpen}
                        size="2xl"
                          w="full"
                        py={8}
                        fontSize="xl"
                        fontWeight="bold"
                        borderRadius="xl"
                        bg={signature ? "green.500" : "gray.200"}
                        color={signature ? "white" : "gray.600"}
                        border="3px solid"
                        borderColor={signature ? "green.600" : "gray.300"}
                        _hover={{
                          bg: signature ? "green.600" : "gray.300",
                          transform: "scale(1.02)"
                        }}
                        leftIcon={<Icon as={signature ? FaCheck : FaSignature} boxSize={6} />}
                      >
                        {signature ? "Signature Captured ✓" : "Click to Sign Document"}
                        </Button>
                      </FormControl>

                      {/* LARGE OBVIOUS SUBMIT BUTTON */}
                      <Box bg="red.100" p={4} borderRadius="xl" border="3px solid red.500">
                      <Button
                        type="submit"
                          colorScheme="red"
                          size="2xl"
                        w="full"
                        isLoading={loading}
                        loadingText="Processing Agreement..."
                          leftIcon={<Icon as={FaCheck} boxSize={10} />}
                          py={16}
                          fontSize="3xl"
                          fontWeight="black"
                          borderRadius="2xl"
                          bg="red.500"
                          color="white"
                          boxShadow="2xl"
                          _hover={{ bg: "red.600", transform: "scale(1.05)" }}
                          _active={{ transform: "scale(0.95)" }}
                          minH="100px"
                        >
                          🚀 CLICK HERE TO SUBMIT 🚀
                      </Button>
                      </Box>

                    </VStack>
                  </form>
                </CardBody>
              </Card>

          
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => setCurrentStep(1)}
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
            size="lg"
            leftIcon={<Icon as={FaFileContract} />}
          >
            ← Back to Agreement
          </Button>
        </VStack>
      </Box>

      {/* Floating Progress Overlay */}
      <Box
        position="fixed"
        top={6}
        right={6}
        bg="whiteAlpha.900"
        backdropFilter="blur(20px)"
        borderRadius="2xl"
        p={6}
        border="2px solid"
        borderColor="green.200"
        boxShadow="2xl"
        zIndex={10}
      >
        <VStack spacing={4}>
          <HStack spacing={3}>
            <Icon as={FaSignature} boxSize={5} color="green.500" />
            <Text fontSize="md" fontWeight="bold" color="gray.800">
              Setup Progress
            </Text>
          </HStack>
          <Progress 
            value={(currentStep / steps.length) * 100} 
            size="md" 
            borderRadius="full"
            bg="gray.200"
            colorScheme="green"
            w="full"
          />
          <Text fontSize="sm" color="gray.600" fontWeight="bold">
            {Math.round((currentStep / steps.length) * 100)}% Complete
          </Text>
        </VStack>
      </Box>
    </Box>
  );

    return (
            <Box position="relative" w="50vw" h="100vh" overflow="hidden" bg="gray.50" mx="auto">
      {/* Two Column Layout - No Spacing */}
      <HStack spacing={0} h="full" align="stretch">
        {/* Left Column - Document */}
        <Box 
          w="60%" 
          h="full" 
          bg="white" 
          borderRight="2px solid" 
          borderColor="blue.200"
          position="relative"
          display="flex"
          flexDirection="column"
        >
          {/* Ultra Compact Document Header */}
          <Box
            w="full"
            bg="linear-gradient(135deg, blue.600, blue.800)"
            color="white"
            py={2}
            px={4}
            flexShrink={0}
          >
            <HStack spacing={3} justify="center" align="center">
              <Icon as={FaFileContract} boxSize={6} color="white" />
              <VStack spacing={0} align="center">
                <Heading size="md" fontWeight="black" lineHeight="1.2">
                  LABORATORY PARTNERSHIP AGREEMENT
                </Heading>
                <Text fontSize="xs" opacity={0.9}>
                  Official Legal Document - Please Read Carefully
                </Text>
              </VStack>
            </HStack>
          </Box>

                    {/* Scrollable Document Content */}
          <Box
            flex="1"
                    overflowY="auto"
            p={2}
                    fontSize="sm"
            lineHeight="1.4"
            fontFamily="serif"
          >
            <VStack spacing={3} align="stretch">
              {/* Lab Info Card */}
              <Box
                bg="blue.50"
                p={3}
                borderRadius="md"
                border="1px solid"
                borderColor="blue.200"
              >
                <HStack spacing={2}>
                  <Icon as={FaBuilding} boxSize={5} color="blue.600" />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="md" fontWeight="bold" color="gray.800">
                      {effectiveLabData.name}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Laboratory Partner
                    </Text>
                    <Badge colorScheme="green" variant="solid" px={2} py={0} borderRadius="full" fontSize="xs">
                      {effectivePlan.name} Plan - ${effectivePlan.price}/month
                    </Badge>
                  </VStack>
                </HStack>
                  </Box>

              {/* Agreement Text */}
              <Box
                bg="gray.50"
                p={3}
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text whiteSpace="pre-line" color="gray.800">
                  {agreementText}
                </Text>
              </Box>
            </VStack>
          </Box>
        </Box>

                        {/* Right Column - Form */}
        <Box 
          w="40%" 
          h="full" 
          bg="linear-gradient(135deg, blue.600, blue.800)"
          position="relative"
          display="flex"
          flexDirection="column"
        >
          {/* Ultra Compact Form Header */}
          <Box
            bg="rgba(255,255,255,0.1)"
            backdropFilter="blur(20px)"
            p={2}
            borderBottom="1px solid rgba(255,255,255,0.2)"
            flexShrink={0}
          >
            <HStack spacing={2} justify="center" align="center">
              <Box
                bg="white"
                borderRadius="full"
                p={2}
                boxShadow="0 3px 10px rgba(0,0,0,0.2)"
              >
                <Icon as={FaSignature} boxSize={4} color="blue.600" />
              </Box>
              <VStack spacing={0} align="start">
                <Heading size="sm" color="white" fontWeight="black" lineHeight="1.2">
                  Complete Your Setup
                </Heading>
                <Text fontSize="xs" color="whiteAlpha.900">
                  Fill form and sign below
                </Text>
              </VStack>
            </HStack>
          </Box>

          {/* Form Content */}
          <Box flex="1" p={2} overflowY="auto">
            <VStack spacing={2} h="full">
              {/* Compact Form Card */}
              <Card 
                bg="white" 
                borderRadius="md" 
                boxShadow="0 5px 15px rgba(0,0,0,0.1)" 
                w="full" 
                border="1px solid rgba(255,255,255,0.5)"
                flex="1"
              >
                <CardBody p={2}>
                  <form id="lab-agreement-form" onSubmit={handleSubmit}>
                    <VStack spacing={2}>
                      {/* Password Field */}
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" fontWeight="bold" color="gray.800">
                          <HStack spacing={1}>
                            <Icon as={FaKey} color="blue.500" boxSize={3} />
                            <Text>Lab Portal Password</Text>
                          </HStack>
                        </FormLabel>
                        <Input
                          value={formData.customSecretKey}
                          onChange={handleSecretKeyChange}
                          placeholder="Enter 8-16 characters"
                          size="xs"
                          maxLength={16}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          _focus={{ borderColor: "blue.500", bg: "blue.50" }}
                          fontSize="xs"
                          borderRadius="sm"
                        />
                        <Text fontSize="xs" color="gray.600" mt={0}>
                          Password to access lab portal
                        </Text>
                      </FormControl>

                      {/* Signer Name */}
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" fontWeight="bold" color="gray.800">Signer Name</FormLabel>
                        <Input
                          value={formData.signerName}
                          onChange={(e) => setFormData({ ...formData, signerName: e.target.value })}
                          placeholder="Full name of person signing"
                          size="xs"
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          _focus={{ borderColor: "blue.500", bg: "blue.50" }}
                          fontSize="xs"
                          borderRadius="sm"
                        />
                      </FormControl>

                      {/* Title */}
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" fontWeight="bold" color="gray.800">Title/Position</FormLabel>
                        <Input
                          value={formData.signerTitle}
                          onChange={(e) => setFormData({ ...formData, signerTitle: e.target.value })}
                          placeholder="Your title or position"
                          size="xs"
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          _focus={{ borderColor: "blue.500", bg: "blue.50" }}
                          fontSize="xs"
                          borderRadius="sm"
                        />
                      </FormControl>

                      {/* Digital Signature */}
                      <FormControl isRequired>
                        <FormLabel fontSize="xs" fontWeight="bold" color="gray.800">
                          <HStack spacing={1}>
                            <Icon as={FaSignature} color="blue.500" boxSize={3} />
                            <Text>Digital Signature</Text>
                          </HStack>
                        </FormLabel>
                        <Button
                          onClick={onOpen}
                          size="xs"
                          w="full"
                          py={1}
                          fontSize="xs"
                          fontWeight="bold"
                          borderRadius="sm"
                          bg={signature ? "green.500" : "gray.100"}
                          color={signature ? "white" : "gray.700"}
                          border="1px solid"
                          borderColor={signature ? "green.600" : "gray.300"}
                          _hover={{
                            bg: signature ? "green.600" : "gray.200",
                            transform: "scale(1.02)"
                          }}
                          leftIcon={<Icon as={signature ? FaCheck : FaSignature} boxSize={3} />}
                        >
                          {signature ? "Signature Captured ✓" : "Click to Sign Document"}
                        </Button>
                      </FormControl>

                      {/* Progress Indicator */}
                      <VStack spacing={2} w="full">
                        <HStack spacing={2} w="full" justify="center">
                          <Icon as={FaFileContract} boxSize={3} color="gray.600" />
                          <Text fontSize="xs" fontWeight="bold" color="gray.600">
                            Setup Progress
                          </Text>
                        </HStack>
                        <Progress 
                          value={100} 
                          size="sm" 
                          borderRadius="full"
                          bg="gray.200"
                          colorScheme="green"
                          w="full"
                        />
                        <Text fontSize="xs" color="gray.600" fontWeight="bold">
                          Ready to Complete
                        </Text>
                      </VStack>
                    </VStack>
                  </form>
                </CardBody>
              </Card>
        </VStack>
          </Box>

          {/* SUBMIT BUTTON - ALWAYS VISIBLE AT BOTTOM */}
          <Box
            p={2}
            bg="green.500"
            borderTop="2px solid white"
            flexShrink={0}
          >
            <Button
              type="submit"
              form="lab-agreement-form"
              size="sm"
              w="full"
              isLoading={loading}
              loadingText="Processing..."
              leftIcon={<Icon as={FaCheck} boxSize={4} color="white" />}
              py={3}
              fontSize="sm"
              fontWeight="black"
              borderRadius="md"
              bg="green.600"
              color="white"
              border="2px solid white"
              boxShadow="0 5px 15px rgba(0,0,0,0.2)"
              _hover={{ 
                transform: "translateY(-2px)", 
                boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
                bg: "green.700"
              }}
              _active={{ transform: "translateY(-1px)" }}
            >
              🚀 SIGN AGREEMENT & COMPLETE SETUP 🚀
            </Button>
          </Box>
        </Box>
      </HStack>
        
      {/* Enhanced Signature Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
          <ModalContent>
          <ModalHeader bg="blue.50" roundedTop="md">
            <VStack align="start" spacing={1}>
              <HStack>
                <Icon as={FaSignature} color="blue.500" />
                <Text>Digital Signature</Text>
              </HStack>
              <Text fontSize="sm" color="gray.600" fontWeight="normal">
                Please sign in the area below using your mouse or touch device
                </Text>
            </VStack>
          </ModalHeader>
          <ModalBody p={6}>
            <VStack spacing={6}>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Draw your signature in the box below. This will serve as your legal signature on the agreement.
                </Text>
              </Alert>
              
              <Box position="relative" w="full">
                  <SignatureCanvas
                    ref={sigPad}
                    canvasProps={{
                    width: 600,
                    height: 250,
                    className: 'signature-canvas',
                    style: {
                      border: '3px dashed #CBD5E0',
                      borderRadius: '12px',
                      background: '#F7FAFC',
                      width: '100%',
                      height: '250px'
                    }
                  }}
                />
                <Text 
                  position="absolute" 
                  bottom={2} 
                  left={4} 
                  fontSize="xs" 
                  color="gray.400"
                  pointerEvents="none"
                >
                  Signature Area - Click and drag to sign
                </Text>
                </Box>
              
              <HStack spacing={4} w="full" justify="center">
                <Button 
                  onClick={clearSignature} 
                  variant="outline"
                  colorScheme="gray"
                  size="lg"
                  minW="120px"
                >
                    Clear
                  </Button>
                <Button 
                  onClick={saveSignature} 
                  colorScheme="blue"
                  size="lg"
                  minW="120px"
                  leftIcon={<Icon as={FaCheck} />}
                >
                    Save Signature
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
    </Box>
  );
};

export default LabAgreement;